<template>
    <div id="app">
      <div class="page-container">
        <div class="dashboard-header">
          <div class="header-content">
            <router-link to="/dashboard" class="nav-btn">
              <span class="nav-icon">←</span>
              <span>Dashboard</span>
            </router-link>
            <h1>Cybersecurity News</h1>
            <div class="user-section">
              <div class="notifications">
                <span class="notification-icon">🔔</span>
              </div>
              <div class="user-profile">
                <img :src="userPhotoUrl" alt="User" class="user-avatar" />
              </div>
            </div>
          </div>
        </div>
  
        <div class="content-grid" v-if="showFeatured">
          <div
            class="news-card featured high-priority"
            v-for="(news, index) in staticNews"
            :key="index"
          >
            <div class="news-tag">{{ news.tag }}</div>
            <h2>{{ news.title }}</h2>
            <p class="news-meta">{{ news.meta }}</p>
            <p class="news-excerpt">{{ news.excerpt }}</p>
            <button class="read-more" @click="openLink(news.url)">Zum Artikel</button>
          </div>
        </div>
  
        <div id="categories-container">
          <div
            class="subcategory-section"
            v-for="(category, index) in categoriesData"
            :key="index"
          >
            <h2 class="section-title" @click="navigateToCategory(category.name)" style="cursor: pointer;">
              {{ category.name }}
            </h2>
            <div class="articles-container">
              <div class="articles-masonry">
                <div
                  v-for="article in getVisibleArticles(category)"
                  :key="article.id"
                  class="article-card"
                >
                  <div class="article-content">
                    <div class="article-meta">
                      <span class="article-date">{{ formatDate(article.published_at) }}</span>
                      <span v-if="article.publisher_name" class="article-publisher">
                        {{ article.publisher_name }}
                      </span>
                    </div>
                    <h3 class="article-title" @click="openLink(article.url)">
                      {{ article.title }}
                    </h3>
                    <div class="tags-row">
                      <template v-if="article.tags && article.tags.trim() !== ''">
                        <span
                          v-for="(tag, tagIndex) in article.tags.split(',')"
                          :key="tagIndex"
                          class="tag-pill"
                          @click="navigateToTag(tag.trim())"
                        >
                          {{ tag.trim() }}
                        </span>
                      </template>
                      <template v-else>
                        <span class="tag-pill">News</span>
                      </template>
                    </div>
                    <p
                      class="article-excerpt"
                      :style="article.expanded ? 'display: block; -webkit-line-clamp: unset;' : ''"
                    >
                      {{ article.expanded ? article.content : getExcerpt(article.content) }}
                    </p>
                    <div class="article-actions">
                      <button 
                        class="action-btn expand"
                        v-if="article.content && article.content.length > 200"
                        @click="toggleExpand(article)"
                      >
                        {{ article.expanded ? '−' : '+' }}
                      </button>
                      <button class="action-btn read" @click="openLink(article.url)">
                        Lesen →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="carousel-controls" v-if="category.articles.length > 4">
                <button class="carousel-btn prev" @click="prevArticles(category)">
                  &#9664;
                </button>
                <button class="carousel-btn next" @click="nextArticles(category)">
                  &#9654;
                </button>
              </div>
            </div>
            <p v-if="category.articles.length === 0">Keine Artikel gefunden.</p>
          </div>
        </div>
  
        <button v-if="showScrollButton" class="scroll-top-button" @click="scrollToTop">
          ↑
        </button>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'CybersecurityNewsView',
    data() {
      return {
        userPhotoUrl: 'https://via.placeholder.com/40',
        staticNews: [
          {
            tag: 'Breaking',
            title: 'Rising Cyber Tensions Between Major Powers',
            meta: 'Updated 2 hours ago • High Priority',
            excerpt:
              'Analysis of increasing cyber operations and their impact on global security landscape...',
            url: '#'
          },
          {
            tag: 'Analysis',
            title: 'State-Sponsored Activities in Eastern Europe',
            meta: '1 day ago • Medium Priority',
            excerpt:
              'Detailed report on recent cyber operations targeting critical infrastructure...',
            url: '#'
          },
          {
            tag: 'Report',
            title: 'Maritime Sector Cyber Threats',
            meta: '2 days ago • Medium Priority',
            excerpt:
              'Analysis of cyber threats affecting global maritime operations...',
            url: '#'
          }
        ],
        categoriesData: [
          { name: 'Attacks', articles: [], currentIndex: 0 },
          { name: 'Leaks', articles: [], currentIndex: 0 },
          { name: 'Laws', articles: [], currentIndex: 0 }
        ],
        showScrollButton: false,
        showFeatured: false
      };
    },
    methods: {
      formatDate(dateString) {
        const date = new Date(dateString);
        const options = { hour: '2-digit', minute: '2-digit' };
        return (
          date.toLocaleDateString('de-DE') +
          ' ' +
          date.toLocaleTimeString('de-DE', options)
        );
      },
      getExcerpt(content) {
        if (!content) return '';
        const lines = content.split('\n').slice(0, 3);
        let summary = lines.join('\n');
        if (content.length > summary.length) summary += '...';
        return summary;
      },
      toggleExpand(article) {
        article.expanded = !article.expanded;
      },
      openLink(url) {
        const smryUrl = `https://smry.ai/${url}`;
        window.open(smryUrl, '_blank');
      },
      scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      nextArticles(category) {
        const totalSets = Math.ceil(category.articles.length / 4);
        const currentSet = Math.floor(category.currentIndex / 4);
        category.currentIndex = ((currentSet + 1) % totalSets) * 4;
      },
      prevArticles(category) {
        const totalSets = Math.ceil(category.articles.length / 4);
        const currentSet = Math.floor(category.currentIndex / 4);
        category.currentIndex = ((currentSet - 1 + totalSets) % totalSets) * 4;
      },
      getVisibleArticles(category) {
        const start = category.currentIndex;
        let end = start + 4;
        if (end > category.articles.length) {
          end = category.articles.length;
        }
        return category.articles.slice(start, end);
      },
      navigateToTag(tag) {
        this.$router.push({
          path: '/tags',
          query: { tag }
        });
      },
      navigateToCategory(categoryName) {
        this.$router.push({
          path: '/category',
          query: { name: categoryName }
        });
      },
      fetchArticles(category) {
        fetch(`https://api.trj.dev/articles?subcategory=${encodeURIComponent(category.name)}&limit=20`)
          .then(response => response.json())
          .then(data => {
            if (data.articles && data.articles.length > 0) {
              category.articles = data.articles.map(article => {
                article.expanded = false;
                return article;
              });
              category.currentIndex = 0;
            } else {
              category.articles = [];
            }
          })
          .catch(error => {
            console.error(`Fehler beim Laden der Artikel für Subcategory ${category.name}:`, error);
            category.articles = [];
          });
      },
      handleScroll() {
        this.showScrollButton = window.pageYOffset > 300;
      }
    },
    mounted() {
      window.scrollTo(0, 0);
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
      this.categoriesData.forEach(category => {
        this.fetchArticles(category);
      });
      window.addEventListener('scroll', this.handleScroll);
    },
    beforeUnmount() {
      window.removeEventListener('scroll', this.handleScroll);
    }
  };
  </script>
  
  <style scoped>
  
  .page-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100vw;
    min-height: 100vh;
    margin: 0;
    padding: 1rem 0;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: #fff;
  }
  .subcategory-section {
    width: 100%;
    padding: 0.25rem 1rem;
    margin-bottom: 0.5rem; /* Reduced from larger value */
  }
  
  .section-title {
    color: #ffffff;
    font-size: 1.8rem;
    margin: 0.25rem 0; /* Reduced from 0.5rem */
    padding-left: 1rem;
  }
  
  .articles-container {
    margin-bottom: 1rem; /* Reduced from larger value */
  }
  
  .articles-masonry {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 1rem; /* Reduced from 1.5rem */
    padding: 0.5rem 0; /* Reduced from 1rem */
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
  
  .article-card {
    margin-bottom: 0.5rem; /* Reduced from larger value */
  }
  
  .article-content {
    padding: 1rem; /* Reduced from 1.5rem */
 
  }
  </style>